import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.object.to-string.js";
import React, { useMemo, useEffect, useRef } from 'react';
import { Route, Switch, generatePath } from 'react-router-dom';
import { DefaultFormFieldLayout } from 'components/FormField';
import UnsavedChangesPrompt from 'components/UnsavedChangesPrompt';
import { usePlaylistManageContentState } from './usePlaylistManageContentState';
import { PlaylistManageContentEditor } from './Editor';
import { PlaylistManageContentPreview } from './Preview';
import { AddContentModals } from './AddContentModals';
import { StickyFooter } from './StickyFooter';
import { getContentAsTree } from './Editor/utils';
import { checkWasPlaylistStructureChanged } from './Editor/utils/checkWasPlaylistStructureChanged';
function PlaylistManageContentScreen(_ref) {
  var initialPlaylistData = _ref.initialPlaylistData,
    stickyHeaderCompensation = _ref.stickyHeaderCompensation,
    routes = _ref.routes,
    renderCustomAssetPicker = _ref.renderCustomAssetPicker;
  var id = initialPlaylistData.id;
  var basePath = generatePath(routes.root, {
    playlistId: id
  });
  var previewPath = generatePath(routes.preview, {
    playlistId: id
  });
  var reducerState = usePlaylistManageContentState({
    initialContent: initialPlaylistData.content
  });
  var _reducerState = _slicedToArray(reducerState, 3),
    _reducerState$ = _reducerState[0],
    content = _reducerState$.content,
    newContentTarget = _reducerState$.newContentTarget,
    dispatch = _reducerState[1],
    history = _reducerState[2];
  var initialContent = history.initial.content;
  var contentAsTree = useMemo(function () {
    return getContentAsTree(content);
  }, [content]);
  var initialContentAsTree = useMemo(function () {
    return getContentAsTree(initialContent);
  }, [initialContent]);
  var hasPlaylistChanges = useMemo(function () {
    var hasContentChanges = content.some(function (item) {
      return item.changedFields.size > 0;
    });
    if (hasContentChanges) {
      return true;
    }
    return checkWasPlaylistStructureChanged(contentAsTree, initialContentAsTree);
  }, [content, contentAsTree, initialContentAsTree]);

  // When the server content refetches (e.g. after a related playlist was saved
  // elsewhere), reset the editor state if there are no unsaved changes so that
  // server-computed fields like `wouldCauseCircularReference` are picked up.
  // We also skip the reset if the user has an add-content flow open (newContentTarget),
  // as a background refetch should not dismiss the picker modal mid-flow.
  var isInitialRender = useRef(true);
  useEffect(function () {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (!hasPlaylistChanges && !newContentTarget) {
      dispatch({
        type: 'RESET'
      });
    }
    // We intentionally only trigger on server content changes, not on every
    // hasPlaylistChanges/newContentTarget update. dispatch is stable and safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPlaylistData.content]);
  return /*#__PURE__*/React.createElement(DefaultFormFieldLayout, {
    horizontal: false
  }, /*#__PURE__*/React.createElement(UnsavedChangesPrompt, {
    hasUnsavedChanges: hasPlaylistChanges,
    permissablePaths: [basePath, previewPath]
  }), newContentTarget && /*#__PURE__*/React.createElement(AddContentModals, {
    reducerState: reducerState,
    initialPlaylistData: initialPlaylistData,
    renderCustomAssetPicker: renderCustomAssetPicker
  }), /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: routes.root,
    render: function render() {
      return /*#__PURE__*/React.createElement(PlaylistManageContentEditor, {
        stickyHeaderCompensation: stickyHeaderCompensation,
        reducerState: reducerState,
        contentAsTree: contentAsTree,
        playlist: initialPlaylistData
      });
    }
  }), /*#__PURE__*/React.createElement(Route, {
    path: routes.preview,
    render: function render() {
      return /*#__PURE__*/React.createElement(PlaylistManageContentPreview, {
        initialPlaylistData: initialPlaylistData,
        reducerState: reducerState
      });
    }
  })), /*#__PURE__*/React.createElement(StickyFooter, {
    reducerState: reducerState,
    hasPlaylistChanges: hasPlaylistChanges,
    initialPlaylistData: initialPlaylistData,
    routes: routes
  }));
}
export { usePlaylistManageContentState } from './usePlaylistManageContentState';
export { formatContentForEditor } from './Editor/utils/formatContentForEditor';
export default PlaylistManageContentScreen;